export interface Span {
  from: number;
  to: number;
}

// A capturing group's slice of one match, numbered the way a backreference numbers it.
export interface GroupSpan extends Span {
  index: number;
}

export interface MatchSpan extends Span {
  groups: GroupSpan[];
}

export interface MatchResult {
  matches: MatchSpan[];
  // What `new RegExp` said when it refused the pattern, this being the one thing on the page nothing else can explain.
  error: string | null;
  // Set when the cap was reached, and the text past the last match was never searched.
  truncated: boolean;
}

// A document long enough to hold more matches than this is one nobody is reading match by match, and the highlight
// stops being a thing that can be looked at well before the editor stops being able to draw it.
const MAX_MATCHES = 5000;

// What `new RegExp` says of the pattern under the flags that were asked for, which is also the whole of what the page
// can safely ask of a pattern on its own thread: compiling one runs nothing against the text.
export function compileError(source: string, flags: string): string | null {
  try {
    new RegExp(source, flags);
    return null;
  } catch (error) {
    return error instanceof Error ? error.message : String(error);
  }
}

export function findMatches(source: string, flags: string, text: string): MatchResult {
  if (!source) return { matches: [], error: null, truncated: false };

  // Asked under the reader's own flags first, since the message quotes them back and `d` is none of theirs.
  const error = compileError(source, flags);
  if (error) return { matches: [], error, truncated: false };

  // `d` goes on whether it was asked for or not: the offsets it records are what the highlight is drawn from, and it
  // is the one flag that changes nothing about what matches, so a pattern that compiled without it compiles with it.
  const regex = new RegExp(source, flags.includes("d") ? flags : `${flags}d`);

  const matches: MatchSpan[] = [];
  // Sticky on its own advances lastIndex the same way global does, so both are searches for more than one match.
  const repeat = regex.global || regex.sticky;
  let truncated = false;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    matches.push(spanOf(match));
    if (!repeat) break;

    if (matches.length >= MAX_MATCHES) {
      truncated = true;
      break;
    }

    // A match that consumed nothing leaves lastIndex where it started, which would find it again forever.
    if (match[0].length === 0) {
      regex.lastIndex = afterOneCharacter(text, regex.lastIndex, regex.unicode || regex.unicodeSets);
    }
  }

  return { matches, error: null, truncated };
}

function spanOf(match: RegExpExecArray): MatchSpan {
  const groups: GroupSpan[] = [];

  for (let index = 1; index < match.length; index++) {
    const at = match.indices?.[index];
    // A group that took no part in this match has nowhere to be drawn, which is not the same as one that matched "".
    if (at) groups.push({ index, from: at[0], to: at[1] });
  }

  return { from: match.index, to: match.index + match[0].length, groups };
}

// Half a surrogate pair is not a position a match may start at, so unicode mode steps over the whole code point.
function afterOneCharacter(text: string, at: number, unicode: boolean): number {
  const point = unicode ? text.codePointAt(at) : undefined;
  return at + (point !== undefined && point > 0xffff ? 2 : 1);
}

export function summarise(result: MatchResult): string {
  const count = result.matches.length;
  const found = count === 0 ? "No matches" : count === 1 ? "1 match" : `${count} matches`;
  return result.truncated ? `${found}, and the search stopped there` : found;
}
